/* eslint-disable no-useless-catch */
/* eslint-disable prettier/prettier */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from '../../../api';

interface IMenteeObject {
  bio: {
    userId: string;
    workHistory: {
      companyName: string;
      role: string;
    };
    education: {
      university: string;
      major: string;
    };
  };
  displayName: string;
  id: string;
  headshot: string;
}

interface IMentorObject {
  bio: {
    userId: string;
    workHistory: {
      companyName: string;
      role: string;
    };
    education: {
      university: string;
      major: string;
    };
  };
  displayName: string;
  id: string;
  headshot: string;
}

interface IMatches {
  id: string;
  isConfirmed: boolean;
  grpId: string;
  matchStatus: string;
  futureCalls: number;
  pastCalls: number;
  lastConnection: any;
  // Present only for matches created from an AI suggestion (see Match Reasoning).
  aiScore?: number;
  aiOverallAssessment?: string;
  aiFieldComparisons?: { field: string; reasoning: string }[];
  mentee: {
    displayName: string;
    menteeId: string;
    headshot: string;
    goalProgress: number;
    bio: {
      workHistory: {
        companyName: string;
        role: string;
      };
      education: {
        university: string;
        major: string;
      };
    };
  };
  mentor: {
    displayName: string;
    mentorId: string;
    headshot: string;
    bio: {
      workHistory: {
        companyName: string;
        role: string;
      };
      education: {
        university: string;
        major: string;
      };
    };
  };
}
interface IMatch {
  mentees: IMenteeObject[];
  mentors: IMentorObject[];
  matches: IMatches[];
  view?: 'grouped';
  groupedMatches?: GroupedMatchItem[];
  pagination?: {
    pageNumber: number;
    pageSize: number;
    total: number;
  };
}

/** Grouped view: one entry per person with their matches */
export interface GroupedMatchItemMatch {
  id: string;
  matchId: string;
  grpId: string;
  matchStatus: string;
  isConfirmed: boolean;
  lastConnection?: number;
  pastMeetings: number;
  futureMeetings: number;
  // Present only for matches created from an AI suggestion (see Match Reasoning).
  aiScore?: number;
  aiOverallAssessment?: string;
  aiFieldComparisons?: { field: string; reasoning: string }[];
  user: {
    userId: string;
    mid?: string;
    displayName: string;
    headshot?: string;
    bio?: Record<string, unknown>;
    goalProgress?: number;
    role: string;
  };
}

export interface GroupedMatchItem {
  userId: string;
  mid?: string;
  displayName: string;
  headshot?: string;
  bio?: Record<string, unknown>;
  goalProgress?: number;
  roles?: string[];
  stats?: {
    totalMatches: number;
    pastMeetings: number;
    futureMeetings: number;
    lastConnection?: number;
  };
  matches: GroupedMatchItemMatch[];
}

interface IInitialState {
  loading: boolean;
  data: IMatch | undefined;
  error: boolean;
  errorText: string;
}

const initialState: IInitialState = {
  loading: false,
  data: undefined,
  error: false,
  errorText: ''
};

export const fetchMatches = createAsyncThunk(
  'getAllMatches',
  async ({
    orgId,
    groupId,
    name,
    pageNumber,
    pageSize,
    grouped = true
  }: {
    orgId: string;
    groupId: string;
    name?: string;
    pageNumber?: number;
    pageSize?: number;
    grouped?: boolean;
  }) => {
    try {
      const response = await API.getAllMatches({ orgId, groupId, name, pageNumber, pageSize, grouped });
      return { payload: response.data.data, pageNumber };
    } catch (error) {
      throw error;
    }
  }
);

const getAllMatchesSlice = createSlice({
  name: 'getAllMatches',
  initialState,
  reducers: {
    updateMenteesMentors: (state, action) => {
      const mentees = state?.data?.mentees.filter((each) => each.id !== action.payload.menteeId) || [];
      const mentors = state?.data?.mentors.filter((each) => each.id !== action.payload.mentorId) || [];
      const matches = state?.data?.matches || [];
      return { ...state, data: { mentees, mentors, matches, pagination: state.data?.pagination } };
    },

    updateConfirmStatus: (state, action) => {
      const matchId = action.payload;
      if (state.data?.view === 'grouped' && Array.isArray(state.data.groupedMatches)) {
        for (const row of state.data.groupedMatches) {
          const idx = row.matches?.findIndex((m: GroupedMatchItemMatch) => m.id === matchId || m.matchId === matchId);
          if (idx !== undefined && idx >= 0 && row.matches) {
            row.matches[idx] = { ...row.matches[idx], isConfirmed: true };
            return;
          }
        }
        return;
      }
      const mentees = state.data?.mentees || [];
      const mentors = state.data?.mentors || [];
      const matches = (state.data?.matches || []).map((ele) => {
        if (ele.id === matchId) {
          return { ...ele, isConfirmed: true };
        }
        return ele;
      });
      state.data = { ...state.data, mentees, mentors, matches, pagination: state.data?.pagination } as IMatch;
    },
    updateConfirmStatusAll: (state) => {
      if (state.data?.view === 'grouped' && Array.isArray(state.data.groupedMatches)) {
        state.data.groupedMatches = state.data.groupedMatches.map((row) => ({
          ...row,
          matches: (row.matches || []).map((m) => ({ ...m, isConfirmed: true }))
        }));
        return;
      }
      const mentees = state.data?.mentees || [];
      const mentors = state.data?.mentors || [];
      const matches = (state.data?.matches || []).map((ele) => ({
        ...ele,
        isConfirmed: true
      }));
      state.data = { ...state.data, matches, mentees, mentors, pagination: state.data?.pagination } as IMatch;
    },

    endMatch: (state, action) => {
      const matchId = action.payload;
      if (state.data?.view === 'grouped' && Array.isArray(state.data.groupedMatches)) {
        for (const row of state.data.groupedMatches) {
          const idx = row.matches?.findIndex((m: GroupedMatchItemMatch) => m.id === matchId || m.matchId === matchId);
          if (idx !== undefined && idx >= 0 && row.matches) {
            row.matches[idx] = { ...row.matches[idx], matchStatus: 'Inactive' };
            return;
          }
        }
        return;
      }
      const mentees = state.data?.mentees || [];
      const mentors = state.data?.mentors || [];
      const matches = (state.data?.matches || []).map((ele) => {
        if (ele.id === matchId) {
          return { ...ele, matchStatus: 'Inactive' };
        }
        return ele;
      });
      state.data = {
        ...state.data,
        matches,
        mentees,
        mentors,
        pagination: state.data?.pagination
      } as IMatch;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMatches.pending, (state, action) => {
        state.loading = true;
        state.error = false;
        state.errorText = '';
        const pageNumber = action.meta.arg?.pageNumber;
        if (pageNumber == null || pageNumber <= 1) {
          state.data = undefined;
        }
      })
      .addCase(fetchMatches.fulfilled, (state, action) => {
        state.loading = false;
        state.error = false;
        state.errorText = '';
        const { payload, pageNumber } = action.payload as { payload: IMatch & { groupedMatches?: GroupedMatchItem[] }; pageNumber?: number };
        const existing = state.data;
        const hasGrouped = Array.isArray(payload?.groupedMatches);

        if (hasGrouped) {
          if (pageNumber != null && pageNumber > 1 && existing && Array.isArray(existing.groupedMatches) && existing.groupedMatches.length) {
            state.data = {
              mentees: existing.mentees ?? [],
              mentors: existing.mentors ?? [],
              matches: existing.matches ?? [],
              view: 'grouped',
              groupedMatches: [...(existing.groupedMatches || []), ...(payload.groupedMatches || [])],
              pagination: payload?.pagination ?? existing.pagination
            };
          } else {
            state.data = {
              mentees: payload?.mentees ?? existing?.mentees ?? [],
              mentors: payload?.mentors ?? existing?.mentors ?? [],
              matches: payload?.matches ?? existing?.matches ?? [],
              view: 'grouped',
              groupedMatches: payload?.groupedMatches ?? [],
              pagination: payload?.pagination ?? (payload as IMatch)?.pagination
            };
          }
          return;
        }

        if (pageNumber != null && pageNumber > 1 && existing?.matches?.length) {
          state.data = {
            mentees: payload?.mentees ?? existing.mentees ?? [],
            mentors: payload?.mentors ?? existing.mentors ?? [],
            matches: [...(existing.matches || []), ...(payload?.matches || [])],
            pagination: payload?.pagination ?? existing.pagination
          };
        } else {
          state.data = {
            ...payload,
            pagination: payload?.pagination ?? (payload as IMatch)?.pagination
          } as IMatch;
        }
      })
      .addCase(fetchMatches.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.errorText = action.error.message || 'Something went wrong';
        state.data = undefined;
      });
  }
});
export const matchesActions = getAllMatchesSlice.actions;

export default getAllMatchesSlice.reducer;
